const normalizeActionCode = (value) => String(value || "").trim().toLowerCase();
const normalizeModuleCode = (value) => String(value || "").trim().toUpperCase();
const normalizeSubModuleCode = (value) => String(value || "").trim().toUpperCase();

const isTruthyValue = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    return s === "true" || s === "1";
  }
  if (typeof value === "number") return value === 1;
  return Boolean(value);
};

const extractActions = (actionsNode) => {
  const actions = {};

  if (Array.isArray(actionsNode)) {
    actionsNode.forEach((entry) => {
      if (typeof entry === "string") {
        const code = normalizeActionCode(entry);
        if (code) actions[code] = true;
        return;
      }

      if (entry && typeof entry === "object") {
        const code = normalizeActionCode(entry.code);
        if (!code) return;
        const allowed = entry.allowed;
        actions[code] = allowed === undefined ? true : isTruthyValue(allowed);
      }
    });
    return actions;
  }

  if (actionsNode && typeof actionsNode === "object") {
    Object.entries(actionsNode).forEach(([actionCode, allowed]) => {
      const code = normalizeActionCode(actionCode);
      if (!code) return;
      actions[code] = isTruthyValue(allowed);
    });
  }

  return actions;
};

export const normalizePermissions = (rawPermissions) => {
  if (!rawPermissions || typeof rawPermissions !== "object") return {};
  if (rawPermissions?.["*"]?.["*"]?.["*"] === true) {
    return { "*": { "*": { "*": true } } };
  }

  let source = rawPermissions;
  // Unwrap nested privilege structures (e.g., defaultPrivileges -> modules -> MODULE_NAME)
  while (source && typeof source === "object") {
    if (source.defaultPrivileges && typeof source.defaultPrivileges === "object") {
      source = source.defaultPrivileges;
    } else if (source.permissions && typeof source.permissions === "object" && !source.permissions["*"]) {
      source = source.permissions;
    } else if (source.modules && typeof source.modules === "object") {
      source = source.modules;
    } else if (source.privileges && typeof source.privileges === "object") {
      source = source.privileges;
    } else {
      break;
    }
  }

  const normalized = {};

  Object.entries(source).forEach(([moduleCode, moduleNode]) => {
    const moduleKey = normalizeModuleCode(moduleCode);
    if (!moduleKey || !moduleNode || typeof moduleNode !== "object") return;

    const nextModule = {};
    if (moduleNode.all === true) {
      nextModule.all = true;
    }
    // RBAC payload currently comes in either:
    // 1) direct map shape: MODULE -> SUBMODULE -> { action: true } / { "*": true }
    // 2) compact shape: MODULE -> { all, subModules: { SUBMODULE -> { all, actions } } }
    // Normalize both into a single internal map consumed by can()/canByKey().
    const looksLikeDirectMap = Object.keys(moduleNode).some(
      (key) => key !== "all" && key !== "subModules",
    );
    const subModules =
      moduleNode.subModules && typeof moduleNode.subModules === "object"
        ? moduleNode.subModules
        : looksLikeDirectMap
          ? moduleNode
          : {};

    Object.entries(subModules).forEach(([subModuleCode, subModuleNode]) => {
      if (subModuleCode === "all" || subModuleCode === "subModules") return;
      const subModuleKey = normalizeSubModuleCode(subModuleCode);
      if (!subModuleKey || !subModuleNode || typeof subModuleNode !== "object") {
        return;
      }

      const nextSubModule = {};
      if (subModuleNode.all === true) {
        nextSubModule.all = true;
        nextSubModule["*"] = true;
      }

      if (subModuleNode["*"] === true) {
        nextSubModule["*"] = true;
      }

      const actions = extractActions(
        subModuleNode.actions && typeof subModuleNode.actions === "object"
          ? subModuleNode.actions
          : subModuleNode,
      );
      Object.entries(actions).forEach(([actionCode, allowed]) => {
        if (allowed === true) {
          nextSubModule[actionCode] = true;
        }
      });

      if (Object.keys(nextSubModule).length > 0) {
        nextModule[subModuleKey] = nextSubModule;
      }
    });

    if (Object.keys(nextModule).length > 0) {
      normalized[moduleKey] = nextModule;
    }
  });

  return normalized;
};
